/* global TPDirect */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  makeStyles, Button, FormControlLabel, Radio, RadioGroup, withStyles, Hidden,
} from '@material-ui/core';
import _, {
  sortBy, map, filter, groupBy,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import config from '@/configs/config';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@/components/TextField';
import Joi from '@hapi/joi';
import { useForm, Controller } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import formErrorMessage from '@/configs/formErrorMessage';
import { ErrorMessage } from '@hookform/error-message';
import helper from '@/helper';
import deliveryLocationConfig from '@/configs/deliveryLocation';
import { CreditCardField } from '../../../menu/components/productDetailModal/PreOrder';
import { PAYMENT_METHOD } from '../../../preorder/containers/CheckoutPage/components/Checkout';
import { useProgressContext } from '../../../preorder/contexts/useProgressContext';
import Alert from '../alert';
import Header from '../cart/Header';
import Item from '../cart/Item';
import { useShoppingCartContext } from '../../contexts/shoppingCart';
import { genGroupId } from '../cart';
import withTapPay from '../../../hocs/withTapPay';
import { paymentMethodMap } from '../../../menu/containers/PreOrder';
import { useAuthState } from '../../../preorder/plugins/Auth';
import { checkout } from '../../api/api';
import preOrderAPI from '../../../preorder/api/preOrder';
import { useProviderDetailsContext } from '../../contexts/providerDetails';
import {
  findProviderDetails,
} from '../../api/ecDataQueryApi';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    paddingBottom: theme.spacing(17),
    minHeight: 'calc(100vh - 280px)',
    textAlign: 'left',
  },
  header: {
    alignItems: 'stretch',
    display: 'none',
    flexDirection: 'column',
    marginTop: theme.spacing(2),

    [theme.breakpoints.up('sm')]: { display: 'flex' },
  },
  body: { [theme.breakpoints.up('sm')]: { marginTop: theme.spacing(3) } },
  footer: {
    [theme.breakpoints.up('sm')]: {
      alignItems: 'start',
      display: 'flex',
    },
  },
  summaryContainer: {
    alignItems: 'stretch',
    background: '#f9f9f9',
    bottom: 0,
    display: 'flex',
    position: 'fixed',
    width: '100%',
    flexWrap: 'wrap',
    zIndex: 20,
    color: '#535353',
    flexShrink: 0,

    [theme.breakpoints.up('sm')]: {
      alignItems: 'stretch',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      height: 'auto',
      padding: theme.spacing(1),
      position: 'static',
      width: 300,
    },
  },
  checkboxContainer: { alignSelf: 'center' },
  checkbotButton: {
    background: config.themeBackgroundColor,
    borderRadius: 5,
    marginLeft: theme.spacing(3),
    paddingLeft: theme.spacing(2.5),
    color: 'white',
    width: 120,

    [theme.breakpoints.up('sm')]: {
      marginLeft: 0,
      marginTop: theme.spacing(1),
      width: '100%',
    },
  },
  priceContainer: {
    alignSelf: 'center',
    flexBasis: 0,
    flexGrow: 1,
    fontSize: 13,
    fontWeight: 'bold',
    textAlign: 'right',
    height: 50,

    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      display: 'flex',
      fontSize: 16,
      justifyContent: 'space-between',
      marginTop: theme.spacing(1),
      width: '100%',
    },
  },
  paymentInfoContainer: {
    background: '#f2f2f2',
    flexGrow: 1,
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
  },
}));

const PICKUP_METHOD = {
  SELF_PICKUP: 'SELF_PICKUP',
  DELIVERY: 'DELIVERY',
};

const StyledRadio = withStyles(() => ({
  root: { '&$checked': { color: '#009687' } },
  checked: {},
}))(Radio);

const schema = Joi.object({
  city: Joi.string().required(),
  district: Joi.string().required(),
  address: Joi.string().required(),
  recepient: Joi.string().required(),
  phone: Joi.string()
    .pattern(/^09\d{2}-?\d{3}-?\d{3}$/, 'phoneNumber')
    .required()
    .messages({ 'string.pattern.name': '您輸入的手機號碼格式錯誤' }),
});

export default withTapPay(() => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { state: { itemIds } = {} } = useLocation();
  const { shoppingCart } = useShoppingCartContext();
  const { start: startProgress, end: endProgress } = useProgressContext();
  const { token } = useAuthState();

  const data = useMemo(
    () => sortBy(
      filter(
        map(
          groupBy(shoppingCart, genGroupId),
          (value, id) => {
            const [item] = value || [];

            return {
              id,
              data: item,
              amount: value.length,
              items: value,
            };
          }
        ),
        ({ id } = {}) => (itemIds || []).includes(id)
      ),
      'id'
    ),
    [shoppingCart, itemIds]
  );

  const providerKey = useMemo(() => _.get(data, [0, 'data', 'providerKey'], null), [data]);
  const {
    providerDetails: providerDetailsCache,
    addProviderDetails,
  } = useProviderDetailsContext();
  useEffect(() => {
    if (!providerKey || providerDetailsCache[providerKey]) return;
    (async () => {
      try {
        const providerDetailsData = await findProviderDetails([providerKey]);
        providerDetailsData.forEach(({ key, value }) => {
          try {
            addProviderDetails(key, JSON.parse(value));
          } catch (error) {
            console.error(error);
          }
        });
      } catch (error2) {
        console.error(error2);
      }
    })();
  }, [data, providerKey]); //eslint-disable-line

  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHOD.TAP_PAY_3D);
  const [pickupMethod, setPickupMethod] = useState(null);
  const canDelivery = useMemo(
    () => _.get(providerDetailsCache, [providerKey, 'canDelivery'], false),
    [providerKey, providerDetailsCache]
  );
  const canSelfPickup = useMemo(
    () => _.get(providerDetailsCache, [providerKey, 'canSelfPickUp'], false),
    [providerKey, providerDetailsCache]
  );
  const deliveryFee = useMemo(() => ((pickupMethod === PICKUP_METHOD.DELIVERY)
    ? _.get(providerDetailsCache, [providerKey, 'deliveryFee'], 0)
    : 0),
  [pickupMethod, providerKey, providerDetailsCache]);
  useEffect(() => {
    if (!pickupMethod) {
      if (canSelfPickup) setPickupMethod(PICKUP_METHOD.SELF_PICKUP);
      else if (canDelivery) setPickupMethod(PICKUP_METHOD.DELIVERY);
    } else if (
      (pickupMethod === PICKUP_METHOD.SELF_PICKUP && !canSelfPickup)
      || (pickupMethod === PICKUP_METHOD.DELIVERY && !canDelivery)
    ) {
      setPickupMethod(null);
    }
  }, [canDelivery, canSelfPickup, pickupMethod]);

  const [alert, setAlert] = React.useState(undefined);

  const history = useHistory();
  const [subtotalPrice, totalPrice] = useMemo(() => {
    let subtotal = 0;
    let total = 0;
    data.forEach((item) => {
      const { amount, data: itemData } = item || {};
      const { price, sellPrice } = itemData || {};

      subtotal += (sellPrice || price || 0) * (amount || 1);
    });
    total = subtotal;
    total += (deliveryFee || 0);
    return [subtotal, total];
  }, [data, deliveryFee]);

  const startCheckoutProcess = async (e) => {
    try {
      e.preventDefault();
      e.stopPropagation();
    } catch (error) {
      // Ignore this error.
    }
    try {
      const {
        city: cityFromDeliveryFormData,
        district: districtFromDeliveryFormData,
        ...restOfDeliveryFormData
      } = e;
      const delivery = pickupMethod === PICKUP_METHOD.DELIVERY ? {
        district: `${cityFromDeliveryFormData}/${districtFromDeliveryFormData}`,
        ...restOfDeliveryFormData,
      } : {};

      startProgress();

      try {
        const items = data.map(({
          amount,
          data: {
            key,
            name,
            specs,
            provider,
            images,
            sellPrice,
            price,
          },
        }) => ({
          amount,
          key,
          name,
          specs,
          provider,
          images,
          sellPrice,
          price,
        }));

        if (
          paymentMethod === PAYMENT_METHOD.TAP_PAY_3D
          || paymentMethod === PAYMENT_METHOD.TAP_PAY
        ) {
          const cardInfo = await new Promise((res) => {
            TPDirect.card.getPrime(({ status = -1, card = {} }) => {
              if (status < 0) throw new Error(t('ec.credit_card_error'));
              res(card);
            });
          });
          const paymentInfo = {
            cardNumber: `${cardInfo?.bincode}******${cardInfo?.lastfour}`,
            cardToken: cardInfo?.prime,
            cardType: cardInfo?.type,
          };
          const checkoutResult = paymentMethod === PAYMENT_METHOD.TAP_PAY
            ? (await checkout({
              items,
              paymentInfo,
              paymentMethod: paymentMethodMap[paymentMethod],
              token,
              delivery,
              pickupMethod,
            }))
            : (await preOrderAPI.createTapPay3DRequestInfo({
              body: {
                items,
                paymentInfo,
                paymentMethod: paymentMethodMap[paymentMethod],
                token,
                delivery,
                pickupMethod,
                redirectUrl: `${window.location.origin}/ec/checkout/tap-pay-3d-payment?submitTapPay3D=true`,
                preChargeAmount: totalPrice,
              },
            }));
          const {
            status, redirectUrl, orderId, message,
          } = checkoutResult || {};

          if (status !== 'OK' && status !== 'success') throw new Error(message || '交易失敗，請聯繫客人員。');

          if (redirectUrl) {
            const { search, pathname, origin: redirectOrigin } = new URL(redirectUrl);
            if (window.location.origin === redirectOrigin) {
              history.replace(`${pathname}${search}`);
              endProgress();
              return false;
            }
            window.location.href = redirectUrl;
            return false;
          }

          history.replace('/ec/checkout/receipt', {
            items,
            totalPrice,
            orderId,
            urlToGo: '/',
          });
        }

        endProgress();
        return true;
      } catch (e1) {
        endProgress();
        throw e1;
      }
    } catch (e2) {
      console.error(e2);
      setAlert({
        title: t('ec.transaction_failed'),
        message: e2.message,
      });
    }
    endProgress();
    return false;
  };

  const onGoBackClick = () => {
    history.push('/ec/cart');
  };

  const availableLocationDic = useMemo(() => helper.getAvailableLocation({
    defaultLocation: deliveryLocationConfig,
    availableLocationSymbolStrings: ['*'],
  }), []);

  const showDeliveryFormError = useCallback(() => {
    setAlert({
      title: t('ec.transaction_failed'),
      message: t('ec.delivery_information_error'),
    });
  }, [t]);

  const {
    errors,
    control,
    watch,
    handleSubmit,
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      city: '',
      district: '',
      address: '',
      recepient: '',
      phone: '',
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages: formErrorMessage,
    }),
  });

  const watchCity = watch('city', '');

  return (
    <div className={classes.root}>

      <div className={classes.header}>
        <Header isReadOnly onGoBackClick={onGoBackClick} />
      </div>
      <div className={classes.body}>
        {(data || []).map((item) => {
          const { id, data: itemData, amount } = item || {};

          return (
            <Item
              amount={amount || 0}
              isReadOnly
              data={itemData || {}}
              key={id}
            />
          );
        })}
      </div>
      <form onSubmit={
        pickupMethod === PICKUP_METHOD.DELIVERY
          ? handleSubmit(startCheckoutProcess, showDeliveryFormError)
          : startCheckoutProcess
      }
      >
        <div className={classes.footer}>
          <div className={classes.paymentInfoContainer}>
            <div>{t('ec.payment_method')}</div>
            <RadioGroup
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
            >
              <FormControlLabel value={PAYMENT_METHOD.TAP_PAY_3D} control={<StyledRadio />} label={t('ec.payment_method_credit_card')} />
            </RadioGroup>
            {
              (
                paymentMethod === PAYMENT_METHOD.TAP_PAY_3D
                || paymentMethod === PAYMENT_METHOD.TAP_PAY
              ) && (
                <CreditCardField
                  fieldStyle={{ background: '#FFFFFF' }}
                  labels={[
                    t('ec.payment_method_credit_card_number'),
                    t('ec.payment_method_credit_card_expire_date'),
                    t('ec.payment_method_credit_card_ccv'),
                  ]}
                />
              )
            }
            <div>{t('ec.pick_up_method')}</div>
            <RadioGroup
              value={pickupMethod}
              onChange={(event) => setPickupMethod(event.target.value)}
            >
              {
                canSelfPickup && (
                  <FormControlLabel value={PICKUP_METHOD.SELF_PICKUP} control={<StyledRadio />} label={t('ec.pick_up_method_self_pick_up')} />
                )
              }
              {
                canDelivery && (
                  <FormControlLabel value={PICKUP_METHOD.DELIVERY} control={<StyledRadio />} label={t('ec.pick_up_method_delivery')} />
                )
              }
            </RadioGroup>
            {
              pickupMethod === PICKUP_METHOD.DELIVERY && (
                <Box my={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={3}>
                      <Box mb={2}>
                        <Box
                          fontSize="subtitle1.fontSize"
                          fontWeight="fontWeightBold"
                        >
                          宅配資訊：
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        fontSize="body2.fontSize"
                        fontWeight="fontWeightBold"
                        color="grey.700"
                      >
                        宅配收件地址
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        control={control}
                        name="city"
                        onChange={(d) => d}
                        render={({
                          onChange: triggerChange,
                            ...rest // eslint-disable-line
                        }) => (
                          <Select
                              {...rest} // eslint-disable-line
                            input={<TextField />}
                            onChange={(event) => {
                              setValue('district', '');
                              triggerChange(event.target.value);
                            }}
                          >
                            {
                              Object.keys(availableLocationDic).map((location) => (
                                <MenuItem key={location} value={location}>{location}</MenuItem>
                              ))
                            }
                          </Select>
                        )}
                      />
                      <Box mb={3} color="error.dark" fontSize="body2.fontSize">
                        <ErrorMessage errors={errors} name="city" />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        control={control}
                        name="district"
                        as={(
                          <Select
                            input={<TextField />}
                          >
                            {
                              watchCity && Object.keys(availableLocationDic[watchCity])
                                .map((district) => (
                                  <MenuItem key={district} value={district}>{district}</MenuItem>
                                ))
                            }
                          </Select>
                        )}
                      />
                      <Box mb={3} color="error.dark" fontSize="body2.fontSize">
                        <ErrorMessage errors={errors} name="district" />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="address"
                        as={<TextField placeholder="街/巷/號/樓" />}
                      />
                      <Box mb={3} color="error.dark" fontSize="body2.fontSize">
                        <ErrorMessage errors={errors} name="address" />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        fontSize="body2.fontSize"
                        fontWeight="fontWeightBold"
                        color="grey.700"
                      >
                        宅配收件人姓名
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="recepient"
                        as={<TextField />}
                      />
                      <Box mb={3} color="error.dark" fontSize="body2.fontSize">
                        <ErrorMessage errors={errors} name="recepient" />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        fontSize="body2.fontSize"
                        fontWeight="fontWeightBold"
                        color="grey.700"
                      >
                        宅配收件人手機
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="phone"
                        as={<TextField />}
                      />
                      <Box mb={3} color="error.dark" fontSize="body2.fontSize">
                        <ErrorMessage errors={errors} name="phone" />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )
            }
          </div>
          <div className={classes.summaryContainer}>
            <Hidden only="xs">
              <div style={{ marginBottom: 16, color: '#000000' }}>{t('ec.order_summary')}</div>
            </Hidden>

            <div className={classes.priceContainer}>
              <div>
                {t('ec.order_summary_sell_price')}
              </div>
              <div>
                {`$${subtotalPrice.toLocaleString('en-US')}`}
              </div>
            </div>

            {
              deliveryFee ? (
                <div className={classes.priceContainer}>
                  <div>
                    {t('ec.delivery_fee')}
                  </div>
                  <div>
                    {`$${deliveryFee.toLocaleString('en-US')}`}
                  </div>
                </div>
              ) : null
            }

            <div className={classes.priceContainer}>
              <div>
                {t('ec.order_summary_total_price')}
              </div>
              <div style={{ color: '#a80000' }}>
                {`$${totalPrice.toLocaleString('en-US')}`}
              </div>
            </div>
            <Button
              className={classes.checkbotButton}
              variant="contained"
              fullWidth
              style={{ fontWeight: 'bold' }}
              type="submit"
            >
              {t('ec.commit_checkout')}
            </Button>
          </div>
        </div>
      </form>
      {alert && (
        <Alert
          isOpen
          title={alert?.title || ''}
          content={alert?.message || ''}
          buttons={alert?.buttons || (
            <Button fullWidth onClick={() => setAlert(undefined)}>
              {t('ec.confirm')}
            </Button>
          )}
        />
      )}
    </div>
  );
});
